<template>
  <div>
    <ManageAssistantTeacherBar />

    <!-- Top App Bar -->
    <v-toolbar color="primary" dark dense elevation="3">
      <v-toolbar-title class="text-h6">
        <v-icon left>mdi-account-star</v-icon>
        ระบบจัดการครูผู้ช่วย รอบ {{ periodassteachs.periodAssTeachTimes }}/{{
          periodassteachs.periodAssTeachYear
        }}
      </v-toolbar-title>
      <v-spacer></v-spacer>

      <!-- Quick Stats -->
      <v-chip class="mr-2" color="white" text-color="primary" label>
        <v-icon left small>mdi-account-multiple</v-icon>
        ทั้งหมด: {{ statistics.total }}
      </v-chip>

      <v-chip class="mr-2" color="success" dark label>
        <v-icon left small>mdi-check-circle</v-icon>
        อนุมัติ: {{ statistics.approved }}
      </v-chip>

      <v-chip class="mr-4" color="warning" dark label>
        <v-icon left small>mdi-clock-alert</v-icon>
        รอตรวจ: {{ statistics.pending }}
      </v-chip>

      <!-- View Mode Toggle -->
      <v-btn-toggle
        v-model="viewMode"
        mandatory
        dense
        color="white"
        class="mr-2"
      >
        <v-btn value="admin" small>
          <v-icon left small>mdi-cog</v-icon>
          จัดการ
        </v-btn>
        <v-btn value="display" small>
          <v-icon left small>mdi-chart-box</v-icon>
          สถิติ
        </v-btn>
      </v-btn-toggle>

      <v-btn icon @click="toggleFullscreen">
        <v-icon>{{
          isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen"
        }}</v-icon>
      </v-btn>

      <v-btn
        icon
        @click="toggleAutoRefresh"
        :color="autoRefresh ? 'green' : ''"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- Display Mode (Statistics Dashboard) -->
    <div v-if="viewMode === 'display'" class="display-mode">
      <v-container fluid class="pa-6 grey lighten-4">
        <!-- Header Card -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-card class="elevation-8" color="primary" dark>
              <v-card-title class="text-h4 justify-center py-6">
                <v-icon x-large left>mdi-chart-arc</v-icon>
                สถิติครูผู้ช่วยรายงานตัว
              </v-card-title>
              <v-card-subtitle class="text-center text-h6 pb-4">
                รอบที่ {{ periodassteachs.periodAssTeachTimes }} /
                {{ periodassteachs.periodAssTeachYear }}
                <v-chip class="ml-4" color="white" text-color="primary" large>
                  <v-icon left>mdi-clock</v-icon>
                  {{ currentTime }}
                </v-chip>
              </v-card-subtitle>
            </v-card>
          </v-col>
        </v-row>

        <!-- Statistics Cards -->
        <v-row class="mb-4">
          <v-col cols="12" md="2">
            <v-card class="elevation-4" color="blue darken-1" dark>
              <v-card-text class="text-center pa-4">
                <v-icon size="50" class="mb-2">mdi-account-multiple</v-icon>
                <div class="text-h3 font-weight-bold">
                  {{ statistics.total }}
                </div>
                <div class="text-h6">ทั้งหมด</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card class="elevation-4" color="success darken-1" dark>
              <v-card-text class="text-center pa-4">
                <v-icon size="50" class="mb-2">mdi-check-circle</v-icon>
                <div class="text-h3 font-weight-bold">
                  {{ statistics.approved }}
                </div>
                <div class="text-h6">อนุมัติแล้ว</div>
                <v-progress-linear
                  :value="statistics.approvedPercent"
                  color="white"
                  height="6"
                  rounded
                  class="mt-2"
                ></v-progress-linear>
                <div class="text-caption mt-1">
                  {{ statistics.approvedPercent }}%
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card class="elevation-4" color="warning darken-1" dark>
              <v-card-text class="text-center pa-4">
                <v-icon size="50" class="mb-2">mdi-clock-alert</v-icon>
                <div class="text-h3 font-weight-bold">
                  {{ statistics.pending }}
                </div>
                <div class="text-h6">รอตรวจสอบ</div>
                <v-progress-linear
                  :value="statistics.pendingPercent"
                  color="white"
                  height="6"
                  rounded
                  class="mt-2"
                ></v-progress-linear>
                <div class="text-caption mt-1">
                  {{ statistics.pendingPercent }}%
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card class="elevation-4" color="error darken-1" dark>
              <v-card-text class="text-center pa-4">
                <v-icon size="50" class="mb-2">mdi-cancel</v-icon>
                <div class="text-h3 font-weight-bold">
                  {{ statistics.disclaimed }}
                </div>
                <div class="text-h6">สละสิทธิ์</div>
                <v-progress-linear
                  :value="statistics.disclaimedPercent"
                  color="white"
                  height="6"
                  rounded
                  class="mt-2"
                ></v-progress-linear>
                <div class="text-caption mt-1">
                  {{ statistics.disclaimedPercent }}%
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card class="elevation-4" color="purple darken-1" dark>
              <v-card-text class="text-center pa-4">
                <v-icon size="50" class="mb-2">mdi-card-account-details</v-icon>
                <div class="text-h3 font-weight-bold">
                  {{ statistics.hasLicense }}
                </div>
                <div class="text-h6">มีใบประกอบ</div>
                <v-progress-linear
                  :value="statistics.hasLicensePercent"
                  color="white"
                  height="6"
                  rounded
                  class="mt-2"
                ></v-progress-linear>
                <div class="text-caption mt-1">
                  {{ statistics.hasLicensePercent }}%
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card class="elevation-4" color="indigo darken-1" dark>
              <v-card-text class="text-center pa-4">
                <v-icon size="50" class="mb-2">mdi-school</v-icon>
                <div class="text-h3 font-weight-bold">
                  {{ statistics.branches }}
                </div>
                <div class="text-h6">สาขาวิชา</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Charts Row 1 -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-card class="elevation-4">
              <v-card-title class="primary white--text">
                <v-icon left color="white">mdi-chart-donut</v-icon>
                สัดส่วนสถานะรายงานตัว
              </v-card-title>
              <v-card-text>
                <apexchart
                  type="donut"
                  height="350"
                  :options="statusDonutChartOptions"
                  :series="statusDonutChartSeries"
                ></apexchart>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="elevation-4">
              <v-card-title class="primary white--text">
                <v-icon left color="white">mdi-chart-pie</v-icon>
                สัดส่วนวิธีการสอบ
              </v-card-title>
              <v-card-text>
                <apexchart
                  type="pie"
                  height="350"
                  :options="examTypePieChartOptions"
                  :series="examTypePieChartSeries"
                ></apexchart>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Charts Row 2 -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-card class="elevation-4">
              <v-card-title class="primary white--text">
                <v-icon left color="white">mdi-chart-bar</v-icon>
                จำนวนครูแยกตามสาขาวิชา (Top 10)
              </v-card-title>
              <v-card-text>
                <apexchart
                  type="bar"
                  height="350"
                  :options="branchBarChartOptions"
                  :series="branchBarChartSeries"
                ></apexchart>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="elevation-4">
              <v-card-title class="primary white--text">
                <v-icon left color="white">mdi-chart-line</v-icon>
                สถิติใบประกอบวิชาชีพ
              </v-card-title>
              <v-card-text>
                <apexchart
                  type="radialBar"
                  height="350"
                  :options="licenseRadialChartOptions"
                  :series="licenseRadialChartSeries"
                ></apexchart>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Charts Row 3 -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-card class="elevation-4">
              <v-card-title class="primary white--text">
                <v-icon left color="white">mdi-chart-timeline-variant</v-icon>
                เปรียบเทียบสถานะการรายงานตัว
              </v-card-title>
              <v-card-text>
                <apexchart
                  type="bar"
                  height="400"
                  :options="comparisonBarChartOptions"
                  :series="comparisonBarChartSeries"
                ></apexchart>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Top Branches Table -->
        <v-row>
          <v-col cols="12">
            <v-card class="elevation-4">
              <v-card-title class="primary white--text">
                <v-icon left color="white">mdi-trophy</v-icon>
                สาขาวิชาที่มีจำนวนครูมากที่สุด (Top 15)
              </v-card-title>
              <v-card-text class="pa-0">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr class="grey lighten-3">
                        <th class="text-center text-h6">อันดับ</th>
                        <th class="text-h6">รหัสสาขา</th>
                        <th class="text-h6">ชื่อสาขา</th>
                        <th class="text-center text-h6">ทั้งหมด</th>
                        <th class="text-center text-h6">อนุมัติ</th>
                        <th class="text-center text-h6">รอตรวจ</th>
                        <th class="text-center text-h6">สละสิทธิ์</th>
                        <th class="text-center text-h6">%อนุมัติ</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(branch, index) in topBranches"
                        :key="branch.id_branch"
                      >
                        <td class="text-center">
                          <v-chip
                            :color="index < 3 ? 'warning' : 'primary'"
                            dark
                            label
                          >
                            <v-icon v-if="index === 0" left small
                              >mdi-trophy</v-icon
                            >
                            <v-icon v-else-if="index === 1" left small
                              >mdi-medal</v-icon
                            >
                            <v-icon v-else-if="index === 2" left small
                              >mdi-podium</v-icon
                            >
                            <strong>{{ index + 1 }}</strong>
                          </v-chip>
                        </td>
                        <td>
                          <strong>{{ branch.id_branch }}</strong>
                        </td>
                        <td>
                          <strong>{{ branch.name_branch }}</strong>
                        </td>
                        <td class="text-center">
                          <v-chip color="info" dark>{{ branch.total }}</v-chip>
                        </td>
                        <td class="text-center">
                          <v-chip color="success" dark>{{
                            branch.approved
                          }}</v-chip>
                        </td>
                        <td class="text-center">
                          <v-chip color="warning" dark>{{
                            branch.pending
                          }}</v-chip>
                        </td>
                        <td class="text-center">
                          <v-chip color="error" dark>{{
                            branch.disclaimed
                          }}</v-chip>
                        </td>
                        <td class="text-center">
                          <v-progress-circular
                            :value="branch.approvedPercent"
                            :color="
                              branch.approvedPercent > 80
                                ? 'success'
                                : branch.approvedPercent > 50
                                ? 'warning'
                                : 'error'
                            "
                            :size="50"
                            :width="5"
                          >
                            {{ branch.approvedPercent }}%
                          </v-progress-circular>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Admin Mode -->
    <v-container v-else fluid class="admin-mode mt-4">
      <!-- Quick Stats (Mini) -->
      <v-row class="mb-4">
        <v-col cols="12" md="2">
          <v-card class="elevation-2" color="blue lighten-5" height="100%">
            <v-card-text class="pa-3">
              <div class="d-flex align-center">
                <v-avatar color="blue darken-2" size="40" class="mr-2">
                  <v-icon dark small>mdi-account-multiple</v-icon>
                </v-avatar>
                <div>
                  <div
                    class="text-h6 font-weight-bold blue--text text--darken-2"
                  >
                    {{ statistics.total }}
                  </div>
                  <div class="text-caption">ทั้งหมด</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="2">
          <v-card class="elevation-2" color="green lighten-5" height="100%">
            <v-card-text class="pa-3">
              <div class="d-flex align-center">
                <v-avatar color="green darken-2" size="40" class="mr-2">
                  <v-icon dark small>mdi-check-circle</v-icon>
                </v-avatar>
                <div>
                  <div
                    class="text-h6 font-weight-bold green--text text--darken-2"
                  >
                    {{ statistics.approved }}
                  </div>
                  <div class="text-caption">
                    อนุมัติ ({{ statistics.approvedPercent }}%)
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="2">
          <v-card class="elevation-2" color="orange lighten-5" height="100%">
            <v-card-text class="pa-3">
              <div class="d-flex align-center">
                <v-avatar color="orange darken-2" size="40" class="mr-2">
                  <v-icon dark small>mdi-clock-alert</v-icon>
                </v-avatar>
                <div>
                  <div
                    class="text-h6 font-weight-bold orange--text text--darken-2"
                  >
                    {{ statistics.pending }}
                  </div>
                  <div class="text-caption">
                    รอตรวจ ({{ statistics.pendingPercent }}%)
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="2">
          <v-card class="elevation-2" color="red lighten-5" height="100%">
            <v-card-text class="pa-3">
              <div class="d-flex align-center">
                <v-avatar color="red darken-2" size="40" class="mr-2">
                  <v-icon dark small>mdi-cancel</v-icon>
                </v-avatar>
                <div>
                  <div
                    class="text-h6 font-weight-bold red--text text--darken-2"
                  >
                    {{ statistics.disclaimed }}
                  </div>
                  <div class="text-caption">สละสิทธิ์</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="2">
          <v-card class="elevation-2" color="purple lighten-5" height="100%">
            <v-card-text class="pa-3">
              <div class="d-flex align-center">
                <v-avatar color="purple darken-2" size="40" class="mr-2">
                  <v-icon dark small>mdi-card-account-details</v-icon>
                </v-avatar>
                <div>
                  <div
                    class="text-h6 font-weight-bold purple--text text--darken-2"
                  >
                    {{ statistics.hasLicense }}
                  </div>
                  <div class="text-caption">มีใบประกอบ</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="2">
          <v-card class="elevation-2" color="indigo lighten-5" height="100%">
            <v-card-text class="pa-3">
              <div class="d-flex align-center">
                <v-avatar color="indigo darken-2" size="40" class="mr-2">
                  <v-icon dark small>mdi-school</v-icon>
                </v-avatar>
                <div>
                  <div
                    class="text-h6 font-weight-bold indigo--text text--darken-2"
                  >
                    {{ statistics.branches }}
                  </div>
                  <div class="text-caption">สาขาวิชา</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Mini Charts -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-card class="elevation-2">
            <v-card-title class="primary white--text py-2">
              <v-icon left color="white" small>mdi-chart-bar</v-icon>
              <span class="text-subtitle-1">สาขาวิชา (Top 5)</span>
            </v-card-title>
            <v-card-text>
              <apexchart
                type="bar"
                height="180"
                :options="miniBarChartOptions"
                :series="miniBarChartSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="elevation-2">
            <v-card-title class="primary white--text py-2">
              <v-icon left color="white" small>mdi-chart-donut</v-icon>
              <span class="text-subtitle-1">สัดส่วนสถานะ</span>
            </v-card-title>
            <v-card-text>
              <apexchart
                type="donut"
                height="180"
                :options="miniDonutChartOptions"
                :series="statusDonutChartSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Card -->
      <base-material-card
        icon="mdi-account-star"
        :title="
          'ข้อมูลครูผู้ช่วย รายงานตัว รอบที่ : ' +
            periodassteachs.periodAssTeachTimes +
            ' / ' +
            periodassteachs.periodAssTeachYear
        "
        class="px-5 py-3"
      >
        <!-- Bulk Update Section -->
        <v-card class="mb-4 pa-4" outlined>
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <strong>
                  <v-icon left>mdi-update</v-icon>
                  ปรับปรุงข้อมูลแบบกลุ่ม
                </strong>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-menu
                      ref="menu1"
                      v-model="menu1"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          prepend-icon="mdi-calendar"
                          outlined
                          dense
                          v-model="
                            updatemanage_assistant_teachers.mt_date_app_now
                          "
                          label="วันที่เริ่มปฏิบัติงาน"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="
                          updatemanage_assistant_teachers.mt_date_app_now
                        "
                        no-title
                        scrollable
                        locale="th"
                        @input="menu1 = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-menu
                      ref="menu2"
                      v-model="menu2"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          prepend-icon="mdi-calendar"
                          outlined
                          dense
                          v-model="
                            updatemanage_assistant_teachers.mt_date_app_postitionnow
                          "
                          label="วันที่ปฏิบัติงานในตำแหน่ง"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="
                          updatemanage_assistant_teachers.mt_date_app_postitionnow
                        "
                        no-title
                        scrollable
                        locale="th"
                        @input="menu2 = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" md="2">
                    <v-text-field
                      prepend-icon="mdi-file-document"
                      outlined
                      dense
                      label="คำสั่งที่"
                      v-model="updatemanage_assistant_teachers.mt_order_app"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="2">
                    <v-autocomplete
                      :items="mt_status_select"
                      item-text="text"
                      item-value="value"
                      outlined
                      dense
                      label="สถานะ"
                      v-model="updatemanage_assistant_teachers.mt_status"
                    ></v-autocomplete>
                  </v-col>

                  <v-col cols="12" md="2">
                    <v-btn
                      color="success"
                      block
                      @click="updatemanage_assistant_teachersDate"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      บันทึก
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>

        <!-- Filter & Actions -->
        <v-card class="mb-4 pa-4" outlined>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="ค้นหา เลขบัตร, ชื่อ, นามสกุล, สาขา"
                single-line
                hide-details
                dense
                outlined
                clearable
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="statusFilter"
                label="กรองสถานะ"
                outlined
                dense
                clearable
                :items="statusFilterOptions"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="branchFilter"
                label="กรองสาขา"
                outlined
                dense
                clearable
                :items="branchFilterOptions"
                item-text="name_branch"
                item-value="id_branch"
              />
            </v-col>
          </v-row>

          <!-- Action Buttons -->
          <v-row class="mt-2">
            <v-col cols="12">
              <v-btn
                color="primary"
                depressed
                class="mr-2 mb-2"
                @click="manage_assistant_teacherAdd"
              >
                <v-icon left>mdi-plus-circle</v-icon>
                เพิ่มรายการ
              </v-btn>

              <v-btn
                color="success"
                depressed
                class="mr-2 mb-2"
                @click="manage_assistant_teacherCSV"
              >
                <v-icon left>mdi-file-import</v-icon>
                นำเข้าข้อมูล
              </v-btn>

              <v-btn
                color="info"
                depressed
                class="mr-2 mb-2"
                @click="manage_assistant_teacherTotal"
              >
                <v-icon left>mdi-chart-pie</v-icon>
                สรุปสถิติ
              </v-btn>

              <v-btn
                color="warning"
                depressed
                class="mr-2 mb-2"
                @click="manage_assistant_teacherQueryAll"
              >
                <v-icon left>mdi-refresh</v-icon>
                รีเฟรช
              </v-btn>

              <v-btn
                color="purple"
                dark
                depressed
                class="mr-2 mb-2"
                :href="'#/admin/print_manage_assistant_teacher_report_all/'"
                target="_blank"
              >
                <v-icon left>mdi-printer</v-icon>
                พิมพ์รายงาน
              </v-btn>
              <v-btn
                color="teal"
                dark
                depressed
                class="mr-2 mb-2"
                @click="exportToExcel"
              >
                <v-icon left>mdi-file-excel</v-icon>
                ส่งออก Excel (.xlsx)
              </v-btn>
            </v-col>
          </v-row>

          <!-- Column Selector -->
          <v-row class="mt-2">
            <v-col cols="12">
              <v-select
                v-model="selectedHeaders"
                :items="headers"
                label="เลือกคอลัมน์ที่ต้องการแสดง"
                multiple
                outlined
                dense
                return-object
                chips
                small-chips
                deletable-chips
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 5"
                    small
                    close
                    @click:close="removeHeader(item)"
                  >
                    {{ item.text }}
                  </v-chip>
                  <span v-if="index === 5" class="grey--text caption">
                    (+{{ selectedHeaders.length - 5 }} คอลัมน์)
                  </span>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card>

        <!-- Data Table -->
        <v-data-table
          :loading="loading"
          :headers="showHeaders"
          :items="filteredTeachers"
          :search="search"
          group-by="mt_id_branch"
          class="elevation-2"
          :items-per-page="25"
          :footer-props="{
            itemsPerPageOptions: [10, 25, 50, 100, -1],
            showFirstLastPage: true
          }"
        >
          <!-- Group Header -->
          <template v-slot:group.header="{ items, isOpen, toggle }">
            <th :colspan="showHeaders.length" class="group-header">
              <v-btn icon small @click="toggle" class="mr-2">
                <v-icon>{{
                  isOpen ? "mdi-chevron-up" : "mdi-chevron-down"
                }}</v-icon>
              </v-btn>
              <v-chip color="primary" dark label class="mr-2">
                {{ items[0].mt_id_branch }}
              </v-chip>
              <strong>{{ items[0].name_branch }}</strong>
              <v-chip small color="info" dark class="ml-2">
                {{ items.length }} คน
              </v-chip>
            </th>
          </template>

          <!-- Status -->
          <template v-slot:item.mt_status="{ item }">
            <v-chip
              :color="item.mt_status === 'disclaim' ? 'error' : 'success'"
              dark
              small
            >
              <v-icon left small>
                {{
                  item.mt_status === "disclaim"
                    ? "mdi-cancel"
                    : "mdi-check-circle"
                }}
              </v-icon>
              {{ item.mt_status === "disclaim" ? "สละสิทธิ์" : "ปกติ" }}
            </v-chip>
          </template>

          <!-- Transfer Status -->
          <template v-slot:item.mt_transfer_status="{ item }">
            <v-chip
              :color="item.mt_transfer_status === '1' || item.mt_transfer_status === 1 ? 'error' : 'success'"
              dark
              x-small
            >
              {{ item.mt_transfer_status === "1" || item.mt_transfer_status === 1 ? "ปิด" : "เปิด" }}
            </v-chip>
          </template>

          <!-- Exam Type -->
          <template v-slot:item.mt_examtype="{ item }">
            <v-chip
              :color="item.mt_examtype === 'select' ? 'purple' : 'indigo'"
              dark
              small
            >
              {{ item.mt_examtype === "select" ? "คัดเลือก" : "ทั่วไป" }}
            </v-chip>
          </template>

          <!-- Exam Group -->
          <template v-slot:item.mt_examgroup="{ item }">
            <div>
              <v-chip
                :color="item.mt_examgroup === 'southern' ? 'warning' : 'purple'"
                dark
                x-small
                class="mb-1"
              >
                {{ item.mt_examgroup === "southern" ? "จชต." : "ทั่วไป" }}
              </v-chip>
              <br v-if="item.mt_type_personnel" />
              <v-chip
                v-if="item.mt_type_personnel === 'transfer_position'"
                color="orange"
                dark
                x-small
              >
                โอนย้าย
              </v-chip>
              <v-chip
                v-if="item.mt_type_personnel === 'local_dev'"
                color="teal"
                dark
                x-small
              >
                พัฒนาท้องถิ่น
              </v-chip>
            </div>
          </template>

          <!-- Report Status -->
          <template v-slot:item.mt_status_report="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  :color="getReportStatusColor(item.mt_status_report)"
                  size="30"
                >
                  {{ getReportStatusIcon(item.mt_status_report) }}
                </v-icon>
              </template>
              <span>{{ getReportStatusText(item.mt_status_report) }}</span>
            </v-tooltip>
          </template>

          <!-- Check Action -->
          <template v-slot:item.action_check="{ item }">
            <v-btn
              v-if="
                item.mt_status_report === 'confirm' ||
                  item.mt_status_report === 'approve'
              "
              @click="checkData(item.mt_id)"
              icon
              :color="
                item.mt_status_report === 'approve' ? 'success' : 'warning'
              "
            >
              <v-icon>
                {{
                  item.mt_status_report === "approve"
                    ? "mdi-check-circle"
                    : "mdi-circle-outline"
                }}
              </v-icon>
            </v-btn>
          </template>

          <!-- License Status -->
          <template v-slot:item.mt_license_status="{ item }">
            <v-icon
              :color="item.mt_license_status === 'have' ? 'success' : 'error'"
              size="30"
            >
              {{
                item.mt_license_status === "have"
                  ? "mdi-clipboard-check"
                  : "mdi-close-box"
              }}
            </v-icon>
          </template>

          <!-- Files -->
          <template v-slot:item.pt_licence_file="{ item }">
            <v-btn
              v-if="String(item.pt_licence_file).length > 5"
              @click="pdflicenseteachdialogOpen(item.pt_licence_file)"
              icon
              color="info"
            >
              <v-icon>mdi-file-pdf-box</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.mt_transcript="{ item }">
            <v-btn
              v-if="String(item.mt_transcript).length > 5"
              @click="pdfmt_transcriptdialogOpen(item.mt_transcript)"
              icon
              color="info"
            >
              <v-icon>mdi-file-document</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.mt_idcard_copy="{ item }">
            <v-btn
              v-if="String(item.mt_idcard_copy).length > 5"
              @click="pdfmt_idcard_copydialogOpen(item.mt_idcard_copy)"
              icon
              color="info"
            >
              <v-icon>mdi-card-account-details</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.mt_change_name="{ item }">
            <v-btn
              v-if="String(item.mt_change_name).length > 5"
              @click="pdfmt_change_namedialogOpen(item.mt_change_name)"
              icon
              color="info"
            >
              <v-icon>mdi-file-certificate</v-icon>
            </v-btn>
          </template>

          <!-- Dates -->
          <template v-slot:item.pt_ondate="{ item }">
            {{
              item.pt_ondate | moment("add", "543 year") | moment("D MMM YYYY")
            }}
          </template>

          <template v-slot:item.pt_enddate="{ item }">
            {{
              item.pt_enddate | moment("add", "543 year") | moment("D MMM YYYY")
            }}
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="
                !['send', 'receipt', 'missing'].includes(
                  item.mt_admissions_status
                )
              "
              icon
              color="warning"
              @click="manage_assistant_teacherEdit(item.mt_id)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-icon v-else color="grey">mdi-lock</v-icon>
          </template>

          <template v-slot:item.action_s="{ item }">
            <v-btn
              v-if="
                !['send', 'receipt', 'missing'].includes(
                  item.mt_admissions_status
                )
              "
              icon
              color="error"
              @click="manage_assistant_teacherDelete(item.mt_id)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>

          <!-- No Data/Results -->
          <template v-slot:no-data>
            <v-alert type="info" outlined class="ma-4">
              <strong>ไม่พบข้อมูล</strong><br />
              กรุณาเพิ่มข้อมูลครูผู้ช่วย
            </v-alert>
          </template>

          <template v-slot:no-results>
            <v-alert type="warning" outlined class="ma-4">
              ไม่พบผลลัพธ์สำหรับ "<strong>{{ search }}</strong
              >"
            </v-alert>
          </template>
        </v-data-table>
      </base-material-card>
    </v-container>

    <!-- ======== DIALOGS ======== -->

    <!-- Add Dialog -->
    <v-dialog
      v-model="addmanage_assistant_teacherdialog"
      persistent
      max-width="1000px"
    >
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-plus-circle</v-icon>
          เพิ่มข้อมูลครูผู้ช่วย
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="addmanage_assistant_teacherform" lazy-validation>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  outlined
                  dense
                  label="เลขบัตรประชาชน"
                  v-model="addmanage_assistant_teacher.mt_id_card"
                  :rules="[v => !!v || 'กรุณากรอกเลขบัตร']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-autocomplete
                  :items="branchs"
                  item-text="name_branch"
                  item-value="id_branch"
                  outlined
                  dense
                  label="สาขาวิชา"
                  v-model="addmanage_assistant_teacher.mt_id_branch"
                  :rules="[v => !!v || 'กรุณาเลือกสาขา']"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.id_branch }} : {{ item.name_branch }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  outlined
                  dense
                  label="ลำดับที่สอบได้"
                  v-model="addmanage_assistant_teacher.mt_sequence"
                  :rules="[v => !!v || 'กรุณากรอกลำดับ']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="คำนำหน้า"
                  v-model="addmanage_assistant_teacher.mt_title_s"
                  :rules="[v => !!v || 'กรุณากรอก']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="5">
                <v-text-field
                  outlined
                  dense
                  label="ชื่อ"
                  v-model="addmanage_assistant_teacher.mt_frist_name"
                  :rules="[v => !!v || 'กรุณากรอกชื่อ']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="5">
                <v-text-field
                  outlined
                  dense
                  label="นามสกุล"
                  v-model="addmanage_assistant_teacher.mt_last_name"
                  :rules="[v => !!v || 'กรุณากรอกนามสกุล']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="วันเกิด"
                  v-model="addmanage_assistant_teacher.mt_brith_day"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="เดือนเกิด"
                  v-model="addmanage_assistant_teacher.mt_brith_month"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="ปีเกิด"
                  v-model="addmanage_assistant_teacher.mt_brith_year"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="เบอร์โทร"
                  v-model="addmanage_assistant_teacher.mt_tel_p"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="วุฒิ (ย่อ)"
                  v-model="addmanage_assistant_teacher.mt_ed_abb"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  outlined
                  dense
                  label="สาขาวิชาเอก"
                  v-model="addmanage_assistant_teacher.mt_ed_name"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="วันบรรจุ"
                  v-model="addmanage_assistant_teacher.mt_appoin_day"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="เดือนบรรจุ"
                  v-model="addmanage_assistant_teacher.mt_appoin_month"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="ปีบรรจุ"
                  v-model="addmanage_assistant_teacher.mt_appoin_year"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="คำสั่งที่"
                  v-model="addmanage_assistant_teacher.mt_order_app"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="mt_status_select"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="สถานะ"
                  v-model="addmanage_assistant_teacher.mt_status"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="mt_examtype_select"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="วิธีการ"
                  v-model="addmanage_assistant_teacher.mt_examtype"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="mt_examgroup_select"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="เขตพื้นที่"
                  v-model="addmanage_assistant_teacher.mt_examgroup"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="type_personnelApp"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="ประเภทบรรจุ"
                  v-model="addmanage_assistant_teacher.mt_type_personnel"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            large
            outlined
            @click="addmanage_assistant_teacherdialog = false"
          >
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            color="success"
            @click="addmanage_assistant_teacherSubmit"
          >
            <v-icon left>mdi-content-save</v-icon>
            บันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog
      v-model="editmanage_assistant_teacherdialog"
      persistent
      max-width="1000px"
    >
      <v-card>
        <v-card-title class="warning white--text">
          <v-icon left color="white">mdi-pencil</v-icon>
          แก้ไขข้อมูลครูผู้ช่วย
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="editmanage_assistant_teacherform" lazy-validation>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  outlined
                  dense
                  label="เลขบัตรประชาชน"
                  v-model="editmanage_assistant_teacher.mt_id_card"
                  :rules="[v => !!v || 'กรุณากรอกเลขบัตร']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-autocomplete
                  :items="branchs"
                  item-text="name_branch"
                  item-value="id_branch"
                  outlined
                  dense
                  label="สาขาวิชา"
                  v-model="editmanage_assistant_teacher.mt_id_branch"
                  :rules="[v => !!v || 'กรุณาเลือกสาขา']"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.id_branch }} : {{ item.name_branch }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="ลำดับ"
                  v-model="editmanage_assistant_teacher.mt_sequence"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="คำนำหน้า"
                  v-model="editmanage_assistant_teacher.mt_title_s"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  outlined
                  dense
                  label="ชื่อ"
                  v-model="editmanage_assistant_teacher.mt_frist_name"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="นามสกุล"
                  v-model="editmanage_assistant_teacher.mt_last_name"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="วันเกิด"
                  v-model="editmanage_assistant_teacher.mt_brith_day"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="เดือน"
                  v-model="editmanage_assistant_teacher.mt_brith_month"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  outlined
                  dense
                  label="ปี"
                  v-model="editmanage_assistant_teacher.mt_brith_year"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="เบอร์โทร"
                  v-model="editmanage_assistant_teacher.mt_tel_p"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="วุฒิ (ย่อ)"
                  v-model="editmanage_assistant_teacher.mt_ed_abb"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  outlined
                  dense
                  label="สาขาเอก"
                  v-model="editmanage_assistant_teacher.mt_ed_name"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="วันบรรจุ"
                  v-model="editmanage_assistant_teacher.mt_appoin_day"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="เดือนบรรจุ"
                  v-model="editmanage_assistant_teacher.mt_appoin_month"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="ปีบรรจุ"
                  v-model="editmanage_assistant_teacher.mt_appoin_year"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  outlined
                  dense
                  label="คำสั่ง"
                  v-model="editmanage_assistant_teacher.mt_order_app"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="mt_status_select"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="สถานะ"
                  v-model="editmanage_assistant_teacher.mt_status"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="mt_examtype_select"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="วิธีการ"
                  v-model="editmanage_assistant_teacher.mt_examtype"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="mt_examgroup_select"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="เขตพื้นที่"
                  v-model="editmanage_assistant_teacher.mt_examgroup"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="type_personnelApp"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="ประเภทบรรจุ"
                  v-model="editmanage_assistant_teacher.mt_type_personnel"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12">
                <v-radio-group
                  v-model="editmanage_assistant_teacher.mt_transfer_status"
                  row
                  dense
                >
                  <template v-slot:label>
                    <strong>สถานะรับรายงานตัว:</strong>
                  </template>
                  <v-radio value="0" label="เปิด" color="success"></v-radio>
                  <v-radio value="1" label="ปิด" color="error"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            large
            outlined
            @click="editmanage_assistant_teacherdialog = false"
          >
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            color="warning"
            @click="editmanage_assistant_teacherSubmit"
          >
            <v-icon left>mdi-pencil</v-icon>
            บันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <v-dialog
      v-model="deletemanage_assistant_teacherdialog"
      persistent
      max-width="500px"
    >
      <v-card>
        <v-card-title class="error white--text">
          <v-icon left color="white">mdi-delete</v-icon>
          ยืนยันการลบ
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" prominent>
            <strong>คำเตือน!</strong><br />
            คุณต้องการลบข้อมูลครูผู้ช่วยรายนี้หรือไม่?
          </v-alert>
          <div class="text-h6 mt-3">
            {{ editmanage_assistant_teacher.mt_id_card }}<br />
            {{ editmanage_assistant_teacher.mt_title_s
            }}{{ editmanage_assistant_teacher.mt_frist_name }}
            {{ editmanage_assistant_teacher.mt_last_name }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            large
            outlined
            @click="deletemanage_assistant_teacherdialog = false"
          >
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            color="error"
            @click="deletemanage_assistant_teacherSubmit"
          >
            <v-icon left>mdi-delete</v-icon>
            ลบ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CSV Import Dialog -->
    <v-dialog
      v-model="manage_assistant_teacherCSVdialog"
      persistent
      max-width="600px"
    >
      <v-card>
        <v-card-title class="success white--text">
          <v-icon left color="white">mdi-file-import</v-icon>
          นำเข้าข้อมูล CSV
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="manage_assistant_teacherCSVdialogform">
            <v-row>
              <v-col cols="12">
                <v-btn
                  outlined
                  block
                  large
                  color="info"
                  to="/admin/manage_assistant_teacher_import/"
                  target="_blank"
                >
                  <v-icon left>mdi-download</v-icon>
                  ดาวน์โหลดแบบฟอร์ม
                </v-btn>
              </v-col>

              <v-col cols="12">
                <v-file-input
                  v-model="file2"
                  accept=".csv"
                  color="success"
                  counter
                  label="ไฟล์ CSV"
                  placeholder="เลือกไฟล์ .csv"
                  prepend-icon="mdi-paperclip"
                  outlined
                  :show-size="1000"
                >
                  <template v-slot:selection="{ text }">
                    <v-chip color="success" dark label small>
                      {{ text }}
                    </v-chip>
                  </template>
                </v-file-input>
              </v-col>

              <v-col cols="12">
                <v-btn
                  block
                  large
                  color="success"
                  @click="csvManpowerSubmit"
                  :disabled="!file2"
                >
                  <v-icon left>mdi-upload</v-icon>
                  นำเข้าข้อมูล
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            large
            outlined
            @click="manage_assistant_teacherCSVdialog = false"
          >
            <v-icon left>mdi-close</v-icon>
            ปิด
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Statistics Summary Dialog -->
    <v-dialog
      v-model="manage_assistant_teacherCountDialog"
      persistent
      max-width="1000px"
    >
      <v-card>
        <v-card-title class="info white--text">
          <v-icon left color="white">mdi-chart-pie</v-icon>
          สรุปสถิติแยกตามสาขา
        </v-card-title>
        <v-card-text class="pt-4">
          <v-data-table
            :loading="loading"
            :headers="headersCount"
            :items="manage_assistant_teacherCount"
            class="elevation-1"
            :items-per-page="15"
          >
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                color="info"
                @click="
                  matBranchQuery(
                    item.mt_times,
                    item.mt_years,
                    item.mt_id_branch
                  )
                "
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            large
            outlined
            @click="manage_assistant_teacherCountDialog = false"
          >
            <v-icon left>mdi-close</v-icon>
            ปิด
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Check Data Dialog (Compact Version) -->
    <v-dialog v-model="checkDataDialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-account-check</v-icon>
          ตรวจสอบข้อมูลครูผู้ช่วย
          <v-spacer></v-spacer>
          <v-btn icon dark @click="checkDataDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4" style="max-height: 70vh;">
          <v-row>
            <!-- Profile Section -->
            <v-col cols="12" md="4">
              <v-card outlined>
                <v-card-text class="text-center">
                  <v-avatar size="150" class="mb-3">
                    <v-img
                      v-if="
                        String(manage_assistant_teacherid.mt_picprofile)
                          .length > 10
                      "
                      :src="
                        'https://hrvec.ovec.go.th/HRvecfiles/' +
                          manage_assistant_teacherid.mt_picprofile
                      "
                    ></v-img>
                    <v-img
                      v-else
                      src="https://hrvec.ovec.go.th/empty.png"
                    ></v-img>
                  </v-avatar>

                  <h3 class="mb-2">
                    {{ manage_assistant_teacherid.mt_title_s
                    }}{{ manage_assistant_teacherid.mt_frist_name }}
                    {{ manage_assistant_teacherid.mt_last_name }}
                  </h3>

                  <v-chip small class="mb-2">
                    {{ manage_assistant_teacherid.mt_id_card }}
                  </v-chip>

                  <div
                    v-if="
                      String(manage_assistant_teacherid.mt_picprofile).length <
                        10
                    "
                  >
                    <v-file-input
                      v-model="mt_picprofile"
                      accept="image/jpg, image/jpeg"
                      label="อัพโหลดรูป"
                      outlined
                      dense
                      prepend-icon="mdi-camera"
                      class="mt-2"
                    ></v-file-input>
                  </div>

                  <div v-else class="mt-2">
                    <v-btn small color="error" outlined @click="deletePic">
                      <v-icon small left>mdi-delete</v-icon>
                      ลบรูป
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Info Section -->
            <v-col cols="12" md="8">
              <v-card outlined>
                <v-card-text>
                  <v-simple-table dense>
                    <tbody>
                      <tr>
                        <td width="40%"><strong>สาขาวิชา:</strong></td>
                        <td>
                          {{ manage_assistant_teacherid.mt_id_branch }} :
                          {{ manage_assistant_teacherid.name_branch }}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>วุฒิการศึกษา:</strong></td>
                        <td>
                          {{ manage_assistant_teacherid.mt_ed_abb }}
                          {{ manage_assistant_teacherid.mt_ed_name }}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>วันเกิด:</strong></td>
                        <td>
                          {{ manage_assistant_teacherid.mt_brith_day }}/{{
                            manage_assistant_teacherid.mt_brith_month
                          }}/{{ manage_assistant_teacherid.mt_brith_year }}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>เบอร์โทร:</strong></td>
                        <td>
                          <v-text-field
                            v-model="manage_assistant_teacherid.mt_tel_p"
                            dense
                            outlined
                            hide-details
                          ></v-text-field>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card-text>
              </v-card>

              <!-- License Status -->
              <v-card outlined class="mt-3">
                <v-card-text>
                  <v-radio-group
                    v-model="manage_assistant_teacherid.mt_license_status"
                    row
                    dense
                  >
                    <template v-slot:label>
                      <strong>สถานะใบประกอบวิชาชีพ:</strong>
                    </template>
                    <v-radio
                      value="have"
                      label="มีใบประกอบ"
                      color="success"
                    ></v-radio>
                    <v-radio
                      value="donot"
                      label="ไม่มี"
                      color="error"
                    ></v-radio>
                  </v-radio-group>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Documents Section -->
            <v-col cols="12">
              <v-card outlined>
                <v-card-title class="subtitle-1">
                  <v-icon left>mdi-file-document-multiple</v-icon>
                  เอกสารประกอบ
                </v-card-title>
                <v-card-text>
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>เอกสาร</th>
                        <th width="200">สถานะ</th>
                        <th width="150">จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- License File -->
                      <tr
                        v-if="
                          manage_assistant_teacherid.mt_license_status ===
                            'have'
                        "
                      >
                        <td>ใบประกอบวิชาชีพครู</td>
                        <td>
                          <v-chip
                            v-if="
                              String(manage_assistant_teacherid.pt_licence_file)
                                .length > 5
                            "
                            small
                            color="success"
                          >
                            <v-icon small left>mdi-check</v-icon>
                            มีไฟล์
                          </v-chip>
                          <v-chip v-else small color="error">
                            <v-icon small left>mdi-close</v-icon>
                            ไม่มีไฟล์
                          </v-chip>
                        </td>
                        <td>
                          <div
                            v-if="
                              String(manage_assistant_teacherid.pt_licence_file)
                                .length < 5
                            "
                          >
                            <v-file-input
                              v-model="pt_licence_file"
                              accept=".pdf"
                              dense
                              hide-details
                              prepend-icon="mdi-file-pdf-box"
                            ></v-file-input>
                          </div>
                          <div v-else>
                            <v-btn
                              small
                              color="info"
                              outlined
                              class="mr-1"
                              @click="viewpt_licence_file"
                            >
                              <v-icon small>mdi-eye</v-icon>
                            </v-btn>
                            <v-btn
                              small
                              color="error"
                              outlined
                              @click="deleteLicense"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </td>
                      </tr>

                      <!-- Education File -->
                      <tr>
                        <td>ปริญญาบัตร/หนังสือรับรอง</td>
                        <td>
                          <v-chip
                            v-if="
                              String(manage_assistant_teacherid.mt_ed_file)
                                .length > 5
                            "
                            small
                            color="success"
                          >
                            <v-icon small left>mdi-check</v-icon>
                            มีไฟล์
                          </v-chip>
                          <v-chip v-else small color="error">
                            <v-icon small left>mdi-close</v-icon>
                            ไม่มีไฟล์
                          </v-chip>
                        </td>
                        <td>
                          <div
                            v-if="
                              String(manage_assistant_teacherid.mt_ed_file)
                                .length < 5
                            "
                          >
                            <v-file-input
                              v-model="mt_ed_file"
                              accept=".pdf"
                              dense
                              hide-details
                              prepend-icon="mdi-file-pdf-box"
                            ></v-file-input>
                          </div>
                          <div v-else>
                            <v-btn
                              small
                              color="info"
                              outlined
                              class="mr-1"
                              @click="viewed_file_file"
                            >
                              <v-icon small>mdi-eye</v-icon>
                            </v-btn>
                            <v-btn
                              small
                              color="error"
                              outlined
                              @click="deleteed_file"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </td>
                      </tr>

                      <!-- Transcript -->
                      <tr>
                        <td>Transcript</td>
                        <td>
                          <v-chip
                            v-if="
                              String(manage_assistant_teacherid.mt_transcript)
                                .length > 5
                            "
                            small
                            color="success"
                          >
                            <v-icon small left>mdi-check</v-icon>
                            มีไฟล์
                          </v-chip>
                          <v-chip v-else small color="error">
                            <v-icon small left>mdi-close</v-icon>
                            ไม่มีไฟล์
                          </v-chip>
                        </td>
                        <td>
                          <div
                            v-if="
                              String(manage_assistant_teacherid.mt_transcript)
                                .length < 5
                            "
                          >
                            <v-file-input
                              v-model="mt_transcript"
                              accept=".pdf"
                              dense
                              hide-details
                              prepend-icon="mdi-file-pdf-box"
                            ></v-file-input>
                          </div>
                          <div v-else>
                            <v-btn
                              small
                              color="info"
                              outlined
                              class="mr-1"
                              @click="viewmt_transcriptfile"
                            >
                              <v-icon small>mdi-eye</v-icon>
                            </v-btn>
                            <v-btn
                              small
                              color="error"
                              outlined
                              @click="deletmt_transcript"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </td>
                      </tr>

                      <!-- ID Card -->
                      <tr>
                        <td>บัตรประชาชน</td>
                        <td>
                          <v-chip
                            v-if="
                              String(manage_assistant_teacherid.mt_idcard_copy)
                                .length > 5
                            "
                            small
                            color="success"
                          >
                            <v-icon small left>mdi-check</v-icon>
                            มีไฟล์
                          </v-chip>
                          <v-chip v-else small color="error">
                            <v-icon small left>mdi-close</v-icon>
                            ไม่มีไฟล์
                          </v-chip>
                        </td>
                        <td>
                          <div
                            v-if="
                              String(manage_assistant_teacherid.mt_idcard_copy)
                                .length < 5
                            "
                          >
                            <v-file-input
                              v-model="mt_idcard_copy"
                              accept=".pdf"
                              dense
                              hide-details
                              prepend-icon="mdi-file-pdf-box"
                            ></v-file-input>
                          </div>
                          <div v-else>
                            <v-btn
                              small
                              color="info"
                              outlined
                              class="mr-1"
                              @click="viewmt_idcard_copyfile"
                            >
                              <v-icon small>mdi-eye</v-icon>
                            </v-btn>
                            <v-btn
                              small
                              color="error"
                              outlined
                              @click="deletmt_idcard_copy"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </td>
                      </tr>

                      <!-- Name Change -->
                      <tr>
                        <td>เอกสารเปลี่ยนชื่อ (ถ้ามี)</td>
                        <td>
                          <v-chip
                            v-if="
                              String(manage_assistant_teacherid.mt_change_name)
                                .length > 5
                            "
                            small
                            color="success"
                          >
                            <v-icon small left>mdi-check</v-icon>
                            มีไฟล์
                          </v-chip>
                          <v-chip v-else small color="warning">
                            <v-icon small left>mdi-minus</v-icon>
                            ไม่จำเป็น
                          </v-chip>
                        </td>
                        <td>
                          <div
                            v-if="
                              String(manage_assistant_teacherid.mt_change_name)
                                .length < 5
                            "
                          >
                            <v-file-input
                              v-model="mt_change_name"
                              accept=".pdf"
                              dense
                              hide-details
                              prepend-icon="mdi-file-pdf-box"
                            ></v-file-input>
                          </div>
                          <div v-else>
                            <v-btn
                              small
                              color="info"
                              outlined
                              class="mr-1"
                              @click="viewmt_change_namefile"
                            >
                              <v-icon small>mdi-eye</v-icon>
                            </v-btn>
                            <v-btn
                              small
                              color="error"
                              outlined
                              @click="deletmt_change_name"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn large color="success" @click="UpdateProfile">
            <v-icon left>mdi-content-save</v-icon>
            บันทึกข้อมูล
          </v-btn>
          <v-btn large color="primary" @click="UpdateProfileConfirm">
            <v-icon left>mdi-check-circle</v-icon>
            ยืนยันรายการ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Check Dialog -->
    <v-dialog v-model="confirmCheckdialog" max-width="500px">
      <v-card>
        <v-card-title class="warning white--text">
          <v-icon left color="white">mdi-check-circle</v-icon>
          ยืนยันการตรวจสอบ
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="info" outlined>
            ตรวจสอบคุณสมบัติและเอกสารครบถ้วน
          </v-alert>

          <v-radio-group
            v-model="manage_assistant_teacherid.mt_status_report"
            class="mt-3"
          >
            <template v-slot:label>
              <strong>ผลการตรวจสอบ:</strong>
            </template>
            <v-radio value="approve" color="success">
              <template v-slot:label>
                <strong class="success--text"
                  >คุณสมบัติถูกต้อง - อนุมัติ</strong
                >
              </template>
            </v-radio>
            <v-radio value="cc" color="error">
              <template v-slot:label>
                <strong class="error--text">ยกเลิกรายการ</strong>
              </template>
            </v-radio>
          </v-radio-group>

          <v-text-field
            v-if="manage_assistant_teacherid.mt_status_report === 'cc'"
            outlined
            label="เหตุผลการยกเลิก"
            v-model="manage_assistant_teacherid.mt_status_comment"
            class="mt-3"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn large outlined @click="confirmCheckdialog = false">
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn large color="success" @click="UpdateCheckConfirm">
            <v-icon left>mdi-content-save</v-icon>
            บันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- PDF Viewers -->
    <v-dialog v-model="pdflicenseteachdialog" max-width="90%">
      <v-card>
        <v-card-title>
          <span>ใบประกอบวิชาชีพ</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="pdflicenseteachdialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <embed
            :src="
              '/HRvecLicenseTeach/' + manage_assistant_teacherid.pt_licence_file
            "
            width="100%"
            height="700px"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdflicenseteachdialog_2" max-width="90%">
      <v-card>
        <v-card-title>
          <span>ใบประกอบวิชาชีพ</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="pdflicenseteachdialog_2 = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <embed
            :src="'/HRvecLicenseTeach/' + pt_licence_files"
            width="100%"
            height="700px"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdfeducationdialog" max-width="90%">
      <v-card>
        <v-card-title>
          <span>ปริญญาบัตร</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="pdfeducationdialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <embed
            :src="'/HREducation/' + manage_assistant_teacherid.mt_ed_file"
            width="100%"
            height="700px"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdftranscriptdialog" max-width="90%">
      <v-card>
        <v-card-title>
          <span>Transcript</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="pdftranscriptdialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <embed
            :src="'/HREducation/' + manage_assistant_teacherid.mt_transcript"
            width="100%"
            height="700px"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdftranscriptdialog_2" max-width="90%">
      <v-card>
        <v-card-title>
          <span>Transcript</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="pdftranscriptdialog_2 = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <embed
            :src="'/HREducation/' + mt_transcript"
            width="100%"
            height="700px"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdfidcarddialog" max-width="90%">
      <v-card>
        <v-card-title>
          <span>บัตรประชาชน</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="pdfidcarddialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <embed
            :src="'/HRIDcard/' + manage_assistant_teacherid.mt_idcard_copy"
            width="100%"
            height="700px"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdfidcarddialog_2" max-width="90%">
      <v-card>
        <v-card-title>
          <span>บัตรประชาชน</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="pdfidcarddialog_2 = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <embed
            :src="'/HRIDcard/' + mt_idcard_copy"
            width="100%"
            height="700px"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdfidcardChangedialog" max-width="90%">
      <v-card>
        <v-card-title>
          <span>เอกสารเปลี่ยนชื่อ</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="pdfidcardChangedialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <embed
            :src="'/HRIDcard/' + manage_assistant_teacherid.mt_change_name"
            width="100%"
            height="700px"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdfidcardChangedialog_2" max-width="90%">
      <v-card>
        <v-card-title>
          <span>เอกสารเปลี่ยนชื่อ</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="pdfidcardChangedialog_2 = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <embed
            :src="'/HRIDcard/' + mt_change_name"
            width="100%"
            height="700px"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ManageAssistantTeacherBar from "../../../components/admin/manageAssistantTeacherBar.vue";
import * as XLSX from "xlsx";
export default {
  name: "HRvecManageAssistantTeacher",
  components: {
    ManageAssistantTeacherBar
  },

  data() {
    return {
      loading: true,
      ApiKey: "HRvec2021",

      // View Mode
      viewMode: "admin",
      isFullscreen: false,
      autoRefresh: false,
      refreshInterval: null,
      currentTime: "",

      // Search & Filters
      search: "",
      statusFilter: null,
      branchFilter: null,
      // Dialogs
      addmanage_assistant_teacherdialog: false,
      editmanage_assistant_teacherdialog: false,
      deletemanage_assistant_teacherdialog: false,
      manage_assistant_teacherCSVdialog: false,
      manage_assistant_teacherCountDialog: false,
      checkDataDialog: false,
      confirmCheckdialog: false,
      pdflicenseteachdialog: false,
      pdflicenseteachdialog_2: false,
      pdfeducationdialog: false,
      pdftranscriptdialog: false,
      pdftranscriptdialog_2: false,
      pdfidcarddialog: false,
      pdfidcarddialog_2: false,
      pdfidcardChangedialog: false,
      pdfidcardChangedialog_2: false,

      // Data
      manage_assistant_teachers: [],
      addmanage_assistant_teacher: {},
      editmanage_assistant_teacher: {},
      manage_assistant_teacherid: {},
      manage_assistant_teacherCount: [],
      updatemanage_assistant_teachers: {},
      branchs: [],
      periodassteachs: {},
      file2: null,

      // Files
      mt_picprofile: null,
      pt_licence_file: null,
      mt_ed_file: null,
      mt_transcript: null,
      mt_idcard_copy: null,
      mt_change_name: null,
      pt_licence_files: "",

      // Date
      date: new Date().toISOString().substr(0, 10),
      menu: false,
      menu1: false,
      menu2: false,

      // Headers
      headers: [],
      selectedHeaders: [],
      headersMap: [
        { text: "วิธีการ", value: "mt_examtype", align: "center" },
        { text: "ประเภทกลุ่ม", value: "mt_examgroup", align: "center" },
        { text: "สถานะ", value: "mt_status", align: "center" },
        { text: "เปิด/ปิด", value: "mt_transfer_status", align: "center" },
        { text: "แก้ไข", value: "actions", align: "center", sortable: false },
        { text: "ลบ", value: "action_s", align: "center", sortable: false },
        { text: "รอบที่", value: "mt_times", align: "center" },
        { text: "ปี", value: "mt_years", align: "center" },
        { text: "รหัสบัตร", value: "mt_id_card", align: "left" },
        { text: "สาขา", value: "mt_id_branch", align: "center" },
        { text: "ลำดับ", value: "mt_sequence", align: "center" },
        { text: "คำนำหน้า", value: "mt_title_s", align: "left" },
        { text: "ชื่อ", value: "mt_frist_name", align: "left" },
        { text: "นามสกุล", value: "mt_last_name", align: "left" },
        { text: "สถานะรายงาน", value: "mt_status_report", align: "center" },
        { text: "ตรวจสอบ", value: "action_check", align: "center" },
        { text: "ใบประกอบ", value: "mt_license_status", align: "center" },
        { text: "ไฟล์ใบประกอบ", value: "pt_licence_file", align: "center" },
        { text: "เลขที่", value: "pt_licence", align: "left" },
        { text: "วันออก", value: "pt_ondate", align: "center" },
        { text: "วันหมด", value: "pt_enddate", align: "center" },
        { text: "Transcript", value: "mt_transcript", align: "center" },
        { text: "บัตรปชช", value: "mt_idcard_copy", align: "center" },
        { text: "เปลี่ยนชื่อ", value: "mt_change_name", align: "center" },
        { text: "วันเกิด", value: "mt_brith_day", align: "center" },
        { text: "เดือนเกิด", value: "mt_brith_month", align: "center" },
        { text: "ปีเกิด", value: "mt_brith_year", align: "center" },
        { text: "โทร", value: "mt_tel_p", align: "left" },
        { text: "วุฒิ", value: "mt_ed_abb", align: "left" },
        { text: "เอกวิชา", value: "mt_ed_name", align: "left" },
        { text: "วันบรรจุ", value: "mt_appoin_day", align: "center" },
        { text: "เดือนบรรจุ", value: "mt_appoin_month", align: "center" },
        { text: "ปีบรรจุ", value: "mt_appoin_year", align: "center" },
        { text: "คำสั่ง", value: "mt_order_app", align: "left" }
      ],

      headersCount: [
        { text: "รอบ", value: "mt_times", align: "center" },
        { text: "ปี", value: "mt_years", align: "center" },
        { text: "รหัส", value: "mt_id_branch", align: "center" },
        { text: "สาขา", value: "branchName", align: "left" },
        { text: "จำนวน", value: "branchGroup", align: "center" },
        { text: "แสดง", value: "actions", align: "center" }
      ],

      // Select Options
      mt_status_select: [
        { text: "ปกติ", value: "normal" },
        { text: "สละสิทธิ์", value: "disclaim" }
      ],
      mt_examtype_select: [
        { text: "คัดเลือก", value: "select" },
        { text: "ทั่วไป", value: "recruit" }
      ],
      mt_examgroup_select: [
        { text: "ทั่วไป", value: "general" },
        { text: "จชต.", value: "southern" }
      ],
      type_personnelApp: [
        { text: "พัฒนาท้องถิ่น", value: "local_dev" },
        { text: "โอนย้าย", value: "transfer_position" },
        { text: "ปกติ", value: "" }
      ],

      statusFilterOptions: [
        { text: "ทั้งหมด", value: null },
        { text: "ปกติ", value: "normal" },
        { text: "สละสิทธิ์", value: "disclaim" },
        { text: "อนุมัติแล้ว", value: "approve" },
        { text: "รอตรวจสอบ", value: "confirm" }
      ],

      branchFilterOptions: []
    };
  },
  computed: {
    statistics() {
      const total = this.manage_assistant_teachers.length;
      const approved = this.manage_assistant_teachers.filter(
        t => t.mt_status_report === "approve"
      ).length;
      const pending = this.manage_assistant_teachers.filter(
        t => t.mt_status_report === "confirm"
      ).length;
      const disclaimed = this.manage_assistant_teachers.filter(
        t => t.mt_status === "disclaim"
      ).length;
      const hasLicense = this.manage_assistant_teachers.filter(
        t => t.mt_license_status === "have"
      ).length;
      const uniqueBranches = new Set(
        this.manage_assistant_teachers.map(t => t.mt_id_branch)
      );

      return {
        total,
        approved,
        pending,
        disclaimed,
        hasLicense,
        branches: uniqueBranches.size,
        approvedPercent: total > 0 ? Math.round((approved / total) * 100) : 0,
        pendingPercent: total > 0 ? Math.round((pending / total) * 100) : 0,
        disclaimedPercent:
          total > 0 ? Math.round((disclaimed / total) * 100) : 0,
        hasLicensePercent:
          total > 0 ? Math.round((hasLicense / total) * 100) : 0
      };
    },

    filteredTeachers() {
      let filtered = this.manage_assistant_teachers;

      if (this.statusFilter) {
        filtered = filtered.filter(teacher => {
          if (
            this.statusFilter === "normal" ||
            this.statusFilter === "disclaim"
          ) {
            return teacher.mt_status === this.statusFilter;
          } else {
            return teacher.mt_status_report === this.statusFilter;
          }
        });
      }

      if (this.branchFilter) {
        filtered = filtered.filter(
          teacher => teacher.mt_id_branch === this.branchFilter
        );
      }

      return filtered;
    },

    showHeaders() {
      return this.headers.filter(h => this.selectedHeaders.includes(h));
    },

    // Branch Statistics
    branchStats() {
      const stats = {};
      this.manage_assistant_teachers.forEach(teacher => {
        if (!stats[teacher.mt_id_branch]) {
          stats[teacher.mt_id_branch] = {
            id_branch: teacher.mt_id_branch,
            name_branch: teacher.name_branch,
            total: 0,
            approved: 0,
            pending: 0,
            disclaimed: 0,
            approvedPercent: 0
          };
        }
        stats[teacher.mt_id_branch].total++;
        if (teacher.mt_status_report === "approve") {
          stats[teacher.mt_id_branch].approved++;
        }
        if (teacher.mt_status_report === "confirm") {
          stats[teacher.mt_id_branch].pending++;
        }
        if (teacher.mt_status === "disclaim") {
          stats[teacher.mt_id_branch].disclaimed++;
        }
      });

      Object.values(stats).forEach(branch => {
        branch.approvedPercent =
          branch.total > 0
            ? Math.round((branch.approved / branch.total) * 100)
            : 0;
      });

      return stats;
    },

    topBranches() {
      return Object.values(this.branchStats)
        .sort((a, b) => b.total - a.total)
        .slice(0, 15);
    },

    // Chart - Status Donut
    statusDonutChartSeries() {
      return [
        this.statistics.approved,
        this.statistics.pending,
        this.statistics.disclaimed
      ];
    },

    statusDonutChartOptions() {
      return {
        chart: {
          type: "donut",
          fontFamily: "Sarabun, sans-serif"
        },
        labels: ["อนุมัติแล้ว", "รอตรวจสอบ", "สละสิทธิ์"],
        colors: ["#4CAF50", "#FF9800", "#F44336"],
        legend: {
          position: "bottom",
          fontSize: "14px"
        },
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "ทั้งหมด",
                  fontSize: "18px",
                  fontWeight: 600
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: true
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " คน";
            }
          }
        }
      };
    },

    // Chart - Exam Type Pie
    examTypePieChartSeries() {
      const select = this.manage_assistant_teachers.filter(
        t => t.mt_examtype === "select"
      ).length;
      const recruit = this.manage_assistant_teachers.filter(
        t => t.mt_examtype === "recruit"
      ).length;
      return [select, recruit];
    },

    examTypePieChartOptions() {
      return {
        chart: {
          type: "pie",
          fontFamily: "Sarabun, sans-serif"
        },
        labels: ["คัดเลือก", "ทั่วไป"],
        colors: ["#9C27B0", "#3F51B5"],
        legend: {
          position: "bottom",
          fontSize: "14px"
        },
        dataLabels: {
          enabled: true
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " คน";
            }
          }
        }
      };
    },

    // Chart - Branch Bar
    branchBarChartSeries() {
      const branches = Object.keys(this.branchStats)
        .sort((a, b) => this.branchStats[b].total - this.branchStats[a].total)
        .slice(0, 10);

      return [
        {
          name: "อนุมัติ",
          data: branches.map(b => this.branchStats[b].approved)
        },
        {
          name: "รอตรวจ",
          data: branches.map(b => this.branchStats[b].pending)
        },
        {
          name: "สละสิทธิ์",
          data: branches.map(b => this.branchStats[b].disclaimed)
        }
      ];
    },

    branchBarChartOptions() {
      const branches = Object.keys(this.branchStats)
        .sort((a, b) => this.branchStats[b].total - this.branchStats[a].total)
        .slice(0, 10);

      return {
        chart: {
          type: "bar",
          stacked: true,
          fontFamily: "Sarabun, sans-serif",
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            dataLabels: {
              total: {
                enabled: true,
                style: {
                  fontSize: "13px",
                  fontWeight: 900
                }
              }
            }
          }
        },
        xaxis: {
          categories: branches.map(b => this.branchStats[b].name_branch),
          labels: {
            style: { fontSize: "12px" }
          }
        },
        yaxis: {
          title: { text: "จำนวน (คน)" }
        },
        legend: {
          position: "top",
          fontSize: "14px"
        },
        colors: ["#4CAF50", "#FF9800", "#F44336"],
        dataLabels: {
          enabled: true
        }
      };
    },

    // Chart - License Radial
    licenseRadialChartSeries() {
      return [this.statistics.hasLicensePercent];
    },

    licenseRadialChartOptions() {
      return {
        chart: {
          type: "radialBar",
          fontFamily: "Sarabun, sans-serif"
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: "60%"
            },
            dataLabels: {
              name: {
                fontSize: "18px"
              },
              value: {
                fontSize: "32px",
                fontWeight: 700,
                formatter: function(val) {
                  return val + "%";
                }
              }
            }
          }
        },
        labels: ["มีใบประกอบวิชาชีพ"],
        colors: ["#9C27B0"]
      };
    },

    // Chart - Comparison Bar
    comparisonBarChartSeries() {
      const branches = Object.keys(this.branchStats)
        .sort((a, b) => this.branchStats[b].total - this.branchStats[a].total)
        .slice(0, 15);

      return [
        {
          name: "ทั้งหมด",
          data: branches.map(b => this.branchStats[b].total)
        },
        {
          name: "อนุมัติ",
          data: branches.map(b => this.branchStats[b].approved)
        }
      ];
    },

    comparisonBarChartOptions() {
      const branches = Object.keys(this.branchStats)
        .sort((a, b) => this.branchStats[b].total - this.branchStats[a].total)
        .slice(0, 15);

      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true
          }
        },
        xaxis: {
          categories: branches.map(b => this.branchStats[b].name_branch)
        },
        legend: {
          position: "top",
          fontSize: "14px"
        },
        colors: ["#2196F3", "#4CAF50"],
        dataLabels: {
          enabled: true
        }
      };
    },

    // Mini Charts
    miniBarChartSeries() {
      const branches = Object.keys(this.branchStats)
        .sort((a, b) => this.branchStats[b].total - this.branchStats[a].total)
        .slice(0, 5);

      return [
        {
          name: "จำนวน",
          data: branches.map(b => this.branchStats[b].total)
        }
      ];
    },

    miniBarChartOptions() {
      const branches = Object.keys(this.branchStats)
        .sort((a, b) => this.branchStats[b].total - this.branchStats[a].total)
        .slice(0, 5);

      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: { show: false }
        },
        plotOptions: {
          bar: { horizontal: true }
        },
        xaxis: {
          categories: branches.map(b => this.branchStats[b].name_branch)
        },
        colors: ["#2196F3"],
        dataLabels: { enabled: true }
      };
    },

    miniDonutChartOptions() {
      return {
        chart: {
          type: "donut",
          fontFamily: "Sarabun, sans-serif"
        },
        labels: ["อนุมัติ", "รอตรวจ", "สละสิทธิ์"],
        colors: ["#4CAF50", "#FF9800", "#F44336"],
        legend: {
          position: "bottom",
          fontSize: "12px"
        },
        plotOptions: {
          pie: {
            donut: { size: "60%" }
          }
        },
        dataLabels: { enabled: false }
      };
    },

    time_stamp() {
      return Date.now();
    }
  },
  watch: {
    autoRefresh(val) {
      if (val) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    }
  },
  async created() {
    this.headers = Object.values(this.headersMap);
    this.selectedHeaders = this.headers.filter(h =>
      [
        "mt_examtype",
        "mt_examgroup",
        "mt_status",
        "mt_transfer_status",
        "actions",
        "action_s",
        "mt_id_card",
        "mt_id_branch",
        "mt_sequence",
        "mt_title_s",
        "mt_frist_name",
        "mt_last_name",
        "mt_status_report",
        "action_check"
      ].includes(h.value)
    );
  },
  async mounted() {
    await this.initialize();
    this.updateTime();
    setInterval(this.updateTime, 1000);
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    async initialize() {
      try {
        await this.periodassteachQuery();
        await this.manage_assistant_teacherQueryAll();
        await this.branchQuery();
        await this.matCountQuery();
        this.branchFilterOptions = this.branchs;
      } catch (error) {
        console.error("Init error:", error);
        swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถโหลดข้อมูลได้"
        });
      }
    },
    updateTime() {
      const now = new Date();
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      };
      this.currentTime = now.toLocaleDateString("th-TH", options);
    },

    exportToExcel() {
      // 1. เตรียมข้อมูลจากรายการที่กรองไว้บนหน้าจอ
      const rawData = this.filteredTeachers;

      if (rawData.length === 0) {
        swal.fire({
          icon: "warning",
          title: "ไม่พบข้อมูล",
          text: "ไม่มีข้อมูลในตารางที่สามารถส่งออกได้"
        });
        return;
      }

      // 2. Mapping ข้อมูลเป็นภาษาไทยเพื่อให้ Excel อ่านง่าย
      const data = rawData.map(item => ({
        วิธีการ: item.mt_examtype === "select" ? "คัดเลือก" : "ทั่วไป",
        เขตพื้นที่: item.mt_examgroup === "southern" ? "จชต." : "ทั่วไป",
        ประเภทบรรจุ:
          item.mt_type_personnel === "transfer_position"
            ? "โอนย้าย"
            : item.mt_type_personnel === "local_dev"
            ? "พัฒนาท้องถิ่น"
            : "ปกติ",
        รหัสสาขา: item.mt_id_branch,
        ชื่อสาขาวิชา: item.name_branch,
        ลำดับ: item.mt_sequence,
        เลขบัตรประชาชน: item.mt_id_card,
        คำนำหน้า: item.mt_title_s,
        ชื่อ: item.mt_frist_name,
        นามสกุล: item.mt_last_name,
        สถานะการรายงาน: this.getReportStatusText(item.mt_status_report),
        ใบประกอบวิชาชีพ: item.mt_license_status === "have" ? "มี" : "ไม่มี",
        เลขที่ใบประกอบ: item.pt_licence || "-",
        วุฒิการศึกษา: item.mt_ed_abb,
        วิชาเอก: item.mt_ed_name,
        เบอร์โทรศัพท์: item.mt_tel_p,
        สถานะข้อมูล: item.mt_status === "disclaim" ? "สละสิทธิ์" : "ปกติ"
      }));

      // 3. สร้าง Workbook และ Worksheet
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "รายชื่อครูผู้ช่วย");

      // 4. ปรับความกว้างคอลัมน์อัตโนมัติ
      const colWidths = Object.keys(data[0]).map(key => ({
        wch: Math.max(key.length * 2, 15)
      }));
      ws["!cols"] = colWidths;

      // 5. ตั้งชื่อไฟล์ตาม รอบ/ปี และวันที่ปัจจุบัน
      const fileName = `รายชื่อครูผู้ช่วย_รอบ_${
        this.periodassteachs.periodAssTeachTimes
      }_${
        this.periodassteachs.periodAssTeachYear
      }_${new Date().toISOString().slice(0, 10)}.xlsx`;

      // 6. ส่งออกไฟล์
      XLSX.writeFile(wb, fileName);

      swal.fire({
        icon: "success",
        title: "ส่งออกข้อมูลเรียบร้อย",
        showConfirmButton: false,
        timer: 1500
      });
    },

    toggleAutoRefresh() {
      this.autoRefresh = !this.autoRefresh;
    },

    startAutoRefresh() {
      this.refreshInterval = setInterval(async () => {
        await this.manage_assistant_teacherQueryAll();
      }, 10000);
    },

    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },

    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        this.isFullscreen = true;
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          this.isFullscreen = false;
        }
      }
    },

    removeHeader(item) {
      const index = this.selectedHeaders.indexOf(item);
      if (index >= 0) this.selectedHeaders.splice(index, 1);
    },

    getReportStatusColor(status) {
      switch (status) {
        case "approve":
          return "success";
        case "confirm":
          return "warning";
        default:
          return "error";
      }
    },

    getReportStatusIcon(status) {
      switch (status) {
        case "approve":
          return "mdi-check-circle";
        case "confirm":
          return "mdi-send-check";
        default:
          return "mdi-alert";
      }
    },

    getReportStatusText(status) {
      switch (status) {
        case "approve":
          return "อนุมัติแล้ว";
        case "confirm":
          return "รอตรวจสอบ";
        default:
          return "ยังไม่ยืนยัน";
      }
    },

    // API Methods
    async periodassteachQuery() {
      const result = await this.$http.post("periodassteach.php", {
        ApiKey: this.ApiKey,
        periodAssTeachEnablePro: "1"
      });
      this.periodassteachs = result.data;
    },

    async branchQuery() {
      const result = await this.$http.post("branch.php", {
        ApiKey: this.ApiKey
      });
      this.branchs = result.data;
    },

    async matCountQuery() {
      const result = await this.$http.post("manage_assistant_teacher.php", {
        ApiKey: this.ApiKey,
        mt_times: this.periodassteachs.periodAssTeachTimes,
        mt_years: this.periodassteachs.periodAssTeachYear,
        mt_count: "Ok"
      });
      this.manage_assistant_teacherCount = result.data;
    },

    async manage_assistant_teacherQueryAll() {
      this.loading = true;
      try {
        const result = await this.$http.post("manage_assistant_teacher.php", {
          ApiKey: this.ApiKey,
          mt_times: this.periodassteachs.periodAssTeachTimes,
          mt_years: this.periodassteachs.periodAssTeachYear,
          report_selete: "ok"
        });
        this.manage_assistant_teachers = result.data;
      } finally {
        this.loading = false;
      }
    },

    async matBranchQuery(mt_times, mt_years, mt_id_branch) {
      const result = await this.$http.post("manage_assistant_teacher.php", {
        ApiKey: this.ApiKey,
        mt_times,
        mt_years,
        mt_id_branch
      });
      this.manage_assistant_teachers = result.data;
      this.manage_assistant_teacherCountDialog = false;
    },

    async updatemanage_assistant_teachersDate() {
      this.updatemanage_assistant_teachers.ApiKey = this.ApiKey;
      this.updatemanage_assistant_teachers.mt_times = this.periodassteachs.periodAssTeachTimes;
      this.updatemanage_assistant_teachers.mt_years = this.periodassteachs.periodAssTeachYear;

      const result = await this.$http.post(
        "manage_assistant_teacher.updateadmin.php",
        this.updatemanage_assistant_teachers
      );

      if (result.data.status) {
        swal.fire({
          icon: "success",
          title: "ปรับปรุงข้อมูลเรียบร้อย",
          showConfirmButton: false,
          timer: 1500
        });
        await this.manage_assistant_teacherQueryAll();
      } else {
        swal.fire({
          icon: "warning",
          title: "ดำเนินการผิดพลาด",
          showConfirmButton: false,
          timer: 1500
        });
      }
    },

    async manage_assistant_teacherAdd() {
      this.addmanage_assistant_teacher = {};
      this.addmanage_assistant_teacherdialog = true;
    },

    async addmanage_assistant_teacherSubmit() {
      if (!this.$refs.addmanage_assistant_teacherform.validate()) return;

      this.addmanage_assistant_teacher.ApiKey = this.ApiKey;
      this.addmanage_assistant_teacher.mt_times = this.periodassteachs.periodAssTeachTimes;
      this.addmanage_assistant_teacher.mt_years = this.periodassteachs.periodAssTeachYear;

      const result = await this.$http.post(
        "manage_assistant_teacher.insert.php",
        this.addmanage_assistant_teacher
      );

      if (result.data.status) {
        swal.fire({
          icon: "success",
          title: "บันทึกข้อมูลเรียบร้อย",
          showConfirmButton: false,
          timer: 1500
        });
        await this.manage_assistant_teacherQueryAll();
        this.addmanage_assistant_teacherdialog = false;
      } else {
        swal.fire({
          icon: "warning",
          title: "บันทึกข้อมูลผิดพลาด",
          showConfirmButton: false,
          timer: 1500
        });
      }
    },

    async manage_assistant_teacherEdit(mt_id) {
      const result = await this.$http.post("manage_assistant_teacher.php", {
        ApiKey: this.ApiKey,
        mt_id
      });
      this.editmanage_assistant_teacher = result.data;
      this.editmanage_assistant_teacherdialog = true;
    },

    async editmanage_assistant_teacherSubmit() {
      if (!this.$refs.editmanage_assistant_teacherform.validate()) return;

      this.editmanage_assistant_teacher.ApiKey = this.ApiKey;

      const result = await this.$http.post(
        "manage_assistant_teacher.update.php",
        this.editmanage_assistant_teacher
      );

      if (result.data.status) {
        swal.fire({
          icon: "success",
          title: "แก้ไขข้อมูลเรียบร้อย",
          showConfirmButton: false,
          timer: 1500
        });
        await this.manage_assistant_teacherQueryAll();
        this.editmanage_assistant_teacherdialog = false;
      } else {
        swal.fire({
          icon: "warning",
          title: "ดำเนินการผิดพลาด",
          showConfirmButton: false,
          timer: 1500
        });
      }
    },

    async manage_assistant_teacherDelete(mt_id) {
      const result = await this.$http.post("manage_assistant_teacher.php", {
        ApiKey: this.ApiKey,
        mt_id
      });
      this.editmanage_assistant_teacher = result.data;
      this.deletemanage_assistant_teacherdialog = true;
    },

    async deletemanage_assistant_teacherSubmit() {
      this.editmanage_assistant_teacher.ApiKey = this.ApiKey;

      const result = await this.$http.post(
        "manage_assistant_teacher.delete.php",
        this.editmanage_assistant_teacher
      );

      if (result.data.status) {
        swal.fire({
          icon: "success",
          title: "ลบข้อมูลเรียบร้อย",
          showConfirmButton: false,
          timer: 1500
        });
        await this.manage_assistant_teacherQueryAll();
      } else {
        swal.fire({
          icon: "warning",
          title: "ดำเนินการผิดพลาด",
          showConfirmButton: false,
          timer: 1500
        });
      }

      this.deletemanage_assistant_teacherdialog = false;
    },

    async csvManpowerSubmit() {
      if (!this.file2) {
        swal.fire({
          icon: "warning",
          title: "กรุณาเลือกไฟล์",
          showConfirmButton: false,
          timer: 1500
        });
        return;
      }

      const formData = new FormData();
      const filename = this.time_stamp + ".assistant.csv";
      formData.append("file", this.file2);
      formData.append("filename", "../HRvecfiles/" + filename);
      formData.append("ApiKey", this.ApiKey);

      const result = await this.$http.post(
        "manage_assistant_teacherCSV.php",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      if (result.data.status) {
        swal.fire({
          icon: "success",
          title: "นำเข้าข้อมูลเรียบร้อย",
          showConfirmButton: false,
          timer: 1500
        });
        await this.manage_assistant_teacherQueryAll();
      } else {
        swal.fire({
          icon: "warning",
          title: "นำเข้าข้อมูลผิดพลาด",
          showConfirmButton: false,
          timer: 1500
        });
      }

      this.manage_assistant_teacherCSVdialog = false;
      this.file2 = null;
    },

    async manage_assistant_teacherCSV() {
      this.manage_assistant_teacherCSVdialog = true;
    },

    async manage_assistant_teacherTotal() {
      await this.matCountQuery();
      this.manage_assistant_teacherCountDialog = true;
    },

    async checkData(mt_id) {
      const result = await this.$http.post("manage_assistant_teacher.php", {
        ApiKey: this.ApiKey,
        mt_id
      });
      this.manage_assistant_teacherid = result.data;
      this.checkDataDialog = true;
    },

    async UpdateProfile() {
      this.manage_assistant_teacherid.ApiKey = this.ApiKey;

      // Upload files if any
      const uploads = [
        {
          file: this.mt_picprofile,
          field: "mt_picprofile",
          folder: "HRvecfiles",
          ext: "jpg"
        },
        {
          file: this.pt_licence_file,
          field: "pt_licence_file",
          folder: "HRvecLicenseTeach",
          ext: "pdf"
        },
        {
          file: this.mt_ed_file,
          field: "mt_ed_file",
          folder: "HREducation",
          ext: "pdf"
        },
        {
          file: this.mt_transcript,
          field: "mt_transcript",
          folder: "HREducation",
          ext: "pdf"
        },
        {
          file: this.mt_idcard_copy,
          field: "mt_idcard_copy",
          folder: "HRIDcard",
          ext: "pdf"
        },
        {
          file: this.mt_change_name,
          field: "mt_change_name",
          folder: "HRIDcard",
          ext: "pdf"
        }
      ];

      for (const upload of uploads) {
        if (upload.file) {
          const formData = new FormData();
          const filename = `${this.manage_assistant_teacherid.mt_id_card}.${this.time_stamp}.${upload.ext}`;
          formData.append("ApiKey", this.ApiKey);
          formData.append("file", upload.file);
          formData.append("filename", `../${upload.folder}/${filename}`);

          await this.$http.post("uploadfile.php", formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });

          this.manage_assistant_teacherid[upload.field] = filename;
          this[upload.field] = null;
        }
      }

      const result = await this.$http.post(
        "manage_assistant_teacher.update.php",
        this.manage_assistant_teacherid
      );

      if (result.data.status) {
        swal.fire({
          icon: "success",
          title: "บันทึกข้อมูลเรียบร้อย",
          showConfirmButton: false,
          timer: 1500
        });
        await this.manage_assistant_teacherQueryAll();
      } else {
        swal.fire({
          icon: "warning",
          title: "ดำเนินการผิดพลาด",
          showConfirmButton: false,
          timer: 1500
        });
      }
    },

    async UpdateProfileConfirm() {
      this.confirmCheckdialog = true;
    },

    async UpdateCheckConfirm() {
      this.manage_assistant_teacherid.ApiKey = this.ApiKey;

      const result = await this.$http.post(
        "manage_assistant_teacher.update.php",
        this.manage_assistant_teacherid
      );

      if (result.data.status) {
        swal.fire({
          icon: "success",
          title: "ยืนยันการตรวจสอบเรียบร้อย",
          showConfirmButton: false,
          timer: 1500
        });
        await this.manage_assistant_teacherQueryAll();
      } else {
        swal.fire({
          icon: "warning",
          title: "ดำเนินการผิดพลาด",
          showConfirmButton: false,
          timer: 1500
        });
      }

      this.confirmCheckdialog = false;
      this.checkDataDialog = false;
    },

    // File Delete Methods
    async deletePic() {
      await this.deleteFile(
        this.manage_assistant_teacherid.mt_picprofile,
        "HRvecfiles",
        "mt_picprofile",
        "รูปถ่าย"
      );
    },

    async deleteLicense() {
      await this.deleteFile(
        this.manage_assistant_teacherid.pt_licence_file,
        "HRvecLicenseTeach",
        "pt_licence_file",
        "ใบประกอบวิชาชีพ"
      );
    },

    async deleteed_file() {
      await this.deleteFile(
        this.manage_assistant_teacherid.mt_ed_file,
        "HREducation",
        "mt_ed_file",
        "ปริญญาบัตร"
      );
    },

    async deletmt_transcript() {
      await this.deleteFile(
        this.manage_assistant_teacherid.mt_transcript,
        "HREducation",
        "mt_transcript",
        "Transcript"
      );
    },

    async deletmt_idcard_copy() {
      await this.deleteFile(
        this.manage_assistant_teacherid.mt_idcard_copy,
        "HRIDcard",
        "mt_idcard_copy",
        "บัตรประชาชน"
      );
    },

    async deletmt_change_name() {
      await this.deleteFile(
        this.manage_assistant_teacherid.mt_change_name,
        "HRIDcard",
        "mt_change_name",
        "เอกสารเปลี่ยนชื่อ"
      );
    },

    async deleteFile(filename, folder, field, label) {
      const result = await swal.fire({
        title: `ยืนยันการลบ ${label}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก"
      });

      if (result.isConfirmed) {
        await this.$http.post("deletefile.php", {
          ApiKey: this.ApiKey,
          filename: `../${folder}/${filename}`
        });

        this.manage_assistant_teacherid[field] = "";

        const updateResult = await this.$http.post(
          "manage_assistant_teacher.update.php",
          this.manage_assistant_teacherid
        );

        if (updateResult.data.status) {
          swal.fire({
            icon: "success",
            title: "ลบข้อมูลเรียบร้อย",
            showConfirmButton: false,
            timer: 1500
          });
          await this.manage_assistant_teacherQueryAll();
        } else {
          swal.fire({
            icon: "warning",
            title: "ดำเนินการผิดพลาด",
            showConfirmButton: false,
            timer: 1500
          });
        }
      }
    },

    // File View Methods
    pdflicenseteachdialogOpen(file) {
      this.pt_licence_files = file;
      this.pdflicenseteachdialog_2 = true;
    },

    pdfmt_transcriptdialogOpen(file) {
      this.mt_transcript = file;
      this.pdftranscriptdialog_2 = true;
    },

    pdfmt_idcard_copydialogOpen(file) {
      this.mt_idcard_copy = file;
      this.pdfidcarddialog_2 = true;
    },

    pdfmt_change_namedialogOpen(file) {
      this.mt_change_name = file;
      this.pdfidcardChangedialog_2 = true;
    },

    viewpt_licence_file() {
      this.pdflicenseteachdialog = true;
    },

    viewed_file_file() {
      this.pdfeducationdialog = true;
    },

    viewmt_transcriptfile() {
      this.pdftranscriptdialog = true;
    },

    viewmt_idcard_copyfile() {
      this.pdfidcarddialog = true;
    },

    viewmt_change_namefile() {
      this.pdfidcardChangedialog = true;
    },

    async downloadImage(filename) {
      const url = "https://hrvec.ovec.go.th/HRvecfiles/" + filename;
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "image.jpg");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>
<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;600;700&display=swap");

* {
  font-family: "Sarabun", sans-serif;
}

.group-header {
  background: linear-gradient(90deg, #f5f5f5 0%, #ffffff 100%);
  padding: 12px 16px !important;
  font-size: 16px;
}

.admin-mode {
  padding-top: 20px;
}

.display-mode {
  height: calc(100vh - 48px);
  overflow-y: auto;
  background: #f5f5f5;
}

.v-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}

@media (max-width: 960px) {
  .text-h3 {
    font-size: 2rem !important;
  }

  .text-h4 {
    font-size: 1.5rem !important;
  }
}

@media (max-width: 600px) {
  .v-data-table {
    font-size: 12px;
  }
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
